'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Check, Heart, Loader2, MessageCircle, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getPendingMatchRequesterIds,
  getRelationToUser,
  requestMatch,
  respondToMatchRequest,
} from '@/lib/swipes';

/**
 * "Request to match" — a profile-initiated right swipe. The recipient gets an
 * identified accept/decline notification (unlike the anonymous deck swipe).
 *
 * Rendered on the public profile page, which is ISR-cached HTML: the server
 * always emits the signed-out default, and per-viewer state (own profile,
 * already requested, matched) resolves client-side after hydration.
 */
export function RequestMatchButton({
  targetUserId,
  className = '',
}: {
  targetUserId: string;
  className?: string;
}) {
  const { session, user: authUser } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const seg = pathname?.split('/')[1];
  const locale = seg === 'es' ? 'es' : seg === 'ja' ? 'ja' : 'en';

  const [state, setState] = useState<
    | 'idle'
    | 'loading'
    | 'sending'
    | 'requested'
    | 'matched'
    | 'incoming'
    | 'responding'
    | 'declined'
  >('idle');
  const [matchId, setMatchId] = useState<string | null>(null);

  const viewerId = authUser?.id ?? null;
  const isOwnProfile = viewerId === targetUserId;

  useEffect(() => {
    if (!viewerId || isOwnProfile) return;
    let cancelled = false;
    setState('loading');
    // Both directions: what I've done to them, and whether they've requested
    // me. An incoming request turns this into an accept/decline control.
    Promise.all([
      getRelationToUser(viewerId, targetUserId),
      getPendingMatchRequesterIds(),
    ]).then(([rel, requesters]) => {
      if (cancelled) return;
      if (rel.matchId) {
        setMatchId(rel.matchId);
        setState('matched');
      } else if (requesters.has(targetUserId)) {
        setState('incoming');
      } else if (rel.swipedDirection === 'right') {
        setState('requested');
      } else {
        // No swipe yet, or an old left swipe — a request overrides the latter.
        setState('idle');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [viewerId, isOwnProfile, targetUserId]);

  if (isOwnProfile) return null;

  const handleClick = async () => {
    if (!session || !viewerId) {
      router.push(`/${locale}/login`);
      return;
    }
    if (state !== 'idle') return;
    setState('sending');
    const res = await requestMatch(targetUserId);
    switch (res.status) {
      case 'matched':
      case 'already_matched':
        setMatchId(res.matchId ?? null);
        setState('matched');
        break;
      case 'requested':
      case 'already_requested':
        setState('requested');
        break;
      default:
        setState('idle');
    }
  };

  const handleRespond = async (accept: boolean) => {
    if (state !== 'incoming') return;
    setState('responding');
    const res = await respondToMatchRequest(targetUserId, accept);
    if (!res.ok) {
      setState('incoming');
      return;
    }
    if (accept) {
      setMatchId(res.matchId ?? null);
      setState('matched');
    } else {
      setState('declined');
    }
  };

  if (state === 'incoming' || state === 'responding') {
    const busyNow = state === 'responding';
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={() => handleRespond(true)}
          disabled={busyNow}
          className="flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-accent px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busyNow ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Check size={16} />
          )}
          <span>Accept match</span>
        </button>
        <button
          onClick={() => handleRespond(false)}
          disabled={busyNow}
          aria-label="Decline match request"
          title="Decline"
          className="flex cursor-pointer items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-60"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  if (state === 'declined') {
    return (
      <div
        className={`flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60 ${className}`}
      >
        <X size={16} />
        <span className="font-medium">Declined</span>
      </div>
    );
  }

  if (state === 'matched') {
    return (
      <Link
        href={matchId ? `/${locale}/app/matches/${matchId}` : `/${locale}/app/matches`}
        className={`flex cursor-pointer items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors ${className}`}
      >
        <MessageCircle size={16} className="text-primary" />
        <span className="font-medium">Matched — open chat</span>
      </Link>
    );
  }

  if (state === 'requested') {
    return (
      <div
        className={`flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60 ${className}`}
      >
        <Check size={16} />
        <span className="font-medium">Request sent</span>
      </div>
    );
  }

  const busy = state === 'loading' || state === 'sending';
  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={`flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-accent px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 ${className}`}
    >
      {state === 'sending' ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Heart size={16} />
      )}
      <span>Request to match</span>
    </button>
  );
}

export default RequestMatchButton;
